import { useCallback, useEffect, useState } from "react";
import {
  createAndPayOrder,
  deliverStatusLabel,
  getOrder,
  listCategories,
  listOrders,
  listProducts,
  listShops,
  type Shop,
  type ShopCategory,
  type ShopOrder,
  type ShopProduct,
} from "@/api/shops";
import { errorMessage } from "@/api/auth";
import { useSession } from "@/session/SessionContext";
import { useToast } from "@/toast/ToastContext";
import type { NetworkEnv } from "@/network/env";

type View =
  | { kind: "root" }
  | { kind: "shop"; shop: string }
  | { kind: "category"; shop: string; category: string }
  | { kind: "history"; shop: string }
  | { kind: "delivery"; shop: string; orderId: string; category?: string };

export function ShopsPanel({ networkEnv }: { networkEnv: NetworkEnv }) {
  const { token } = useSession();
  const toast = useToast();
  const [view, setView] = useState<View>({ kind: "root" });
  const [shops, setShops] = useState<Shop[]>([]);
  const [cats, setCats] = useState<ShopCategory[]>([]);
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [orders, setOrders] = useState<ShopOrder[]>([]);
  const [order, setOrder] = useState<ShopOrder | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const loadRoot = useCallback(async () => {
    setErr("");
    setView({ kind: "root" });
    try {
      setShops(await listShops());
    } catch (e) {
      setErr(errorMessage(e));
    }
  }, []);

  useEffect(() => {
    void loadRoot();
  }, [loadRoot]);

  async function openShop(shop: string) {
    setErr("");
    setView({ kind: "shop", shop });
    try {
      setCats(await listCategories(shop));
    } catch (e) {
      setErr(errorMessage(e));
    }
  }

  async function openCategory(shop: string, category: string) {
    setErr("");
    setView({ kind: "category", shop, category });
    try {
      setProducts(await listProducts(shop, category));
    } catch (e) {
      setErr(errorMessage(e));
    }
  }

  async function openHistory(shop: string) {
    if (!token) {
      toast.err("SESSION");
      return;
    }
    setErr("");
    setView({ kind: "history", shop });
    try {
      setOrders(await listOrders(shop, token));
    } catch (e) {
      setErr(errorMessage(e));
    }
  }

  async function openDelivery(shop: string, orderId: string, category?: string) {
    if (!token) return;
    setErr("");
    setView({ kind: "delivery", shop, orderId, category });
    try {
      setOrder(await getOrder(shop, orderId, token));
    } catch (e) {
      setErr(errorMessage(e));
    }
  }

  async function buy(shop: string, category: string, productId: string, payAsset: string) {
    if (!token) {
      toast.err("SESSION");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const out = await createAndPayOrder(shop, token, {
        product_id: productId,
        pay_asset: payAsset,
        networkEnv,
      });
      toast.ok("خرید");
      await openDelivery(shop, out.id, category);
    } catch (e) {
      setErr(errorMessage(e));
      toast.err(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.ok("کپی");
    } catch {
      toast.err("کپی");
    }
  }

  return (
    <section className="panel shops-panel">
      <div className="shops-crumb">
        {view.kind !== "root" && (
          <button
            type="button"
            className="ghost"
            onClick={() => {
              if (view.kind === "shop" || view.kind === "history") void loadRoot();
              else if (view.kind === "category") void openShop(view.shop);
              else if (view.kind === "delivery") {
                if (view.category) void openCategory(view.shop, view.category);
                else void openHistory(view.shop);
              }
            }}
          >
            بازگشت
          </button>
        )}
        <span className="muted">
          {view.kind === "root" && "فروشگاه‌ها"}
          {view.kind === "shop" && view.shop}
          {view.kind === "category" && `${view.shop} / ${view.category}`}
          {view.kind === "history" && "سوابق"}
          {view.kind === "delivery" && "تحویل"}
        </span>
      </div>

      {err && <p className="net-error">{err}</p>}

      {view.kind === "root" && (
        <div className="quick-grid">
          {shops.map((s) => (
            <button
              key={s.code}
              type="button"
              className="action-card"
              onClick={() => void openShop(s.code)}
            >
              <b>{s.title}</b>
              <small className="muted">{s.code}</small>
            </button>
          ))}
          {!shops.length && !err && <p className="muted">۰</p>}
        </div>
      )}

      {view.kind === "shop" && (
        <div className="quick-grid">
          <button type="button" className="action-card" onClick={() => void openHistory(view.shop)}>
            <b>سوابق خرید</b>
          </button>
          {cats.map((c) => (
            <button
              key={c.code}
              type="button"
              className="action-card"
              onClick={() => void openCategory(view.shop, c.code)}
            >
              <b>{c.title}</b>
              <small className="muted">{c.code}</small>
            </button>
          ))}
        </div>
      )}

      {view.kind === "category" && (
        <div className="wallet-grid">
          {!products.length && <p className="muted">۰</p>}
          {products.map((p) => (
            <article key={p.id} className="card">
              <b>{p.title}</b>
              <p className="muted ltr">
                ${String(p.price_hint_usd ?? "—")} · gift#{String(p.gift_product_id ?? "—")}
              </p>
              <p className="muted ltr">{(p.pay_assets || []).join(" · ")}</p>
              <button
                type="button"
                className="primary"
                disabled={busy}
                onClick={() => {
                  const asset = (p.pay_assets && p.pay_assets[0]) || "NUSD";
                  void buy(view.shop, view.category, p.id, asset);
                }}
              >
                {busy ? "…" : `خرید ${(p.pay_assets && p.pay_assets[0]) || "NUSD"}`}
              </button>
            </article>
          ))}
        </div>
      )}

      {view.kind === "history" && (
        <div className="wallet-grid">
          {!orders.length && <p className="muted">۰</p>}
          {orders.map((o) => {
            const st = deliverStatusLabel(o.deliver_status);
            return (
              <button
                key={o.id}
                type="button"
                className="card shops-hist"
                onClick={() => void openDelivery(view.shop, o.id)}
              >
                <div className="row-between">
                  <b>{o.title || o.product_id}</b>
                  <span className={`shops-status ${st.cls}`}>{st.text}</span>
                </div>
                <p className="muted ltr">
                  {o.id} · {String(o.total_usd ?? o.amount_usd)} {o.pay_asset} · fee{" "}
                  {String(o.network_fee_usd ?? "0")}
                </p>
              </button>
            );
          })}
        </div>
      )}

      {view.kind === "delivery" && order && (
        <DeliveryCard order={order} onCopy={(c) => void copy(c)} />
      )}
    </section>
  );
}

function DeliveryCard({ order, onCopy }: { order: ShopOrder; onCopy: (c: string) => void }) {
  const st = deliverStatusLabel(order.deliver_status);
  const codes = order.codes || [];
  return (
    <div className="card shops-delivery">
      <div className={`shops-status ${st.cls}`}>{st.text}</div>
      <b>{order.title || order.product_id}</b>
      <p className="muted ltr">
        {order.id} · {order.status}
      </p>
      <div className="shops-cost ltr">
        <div>
          amount {String(order.amount_usd)} {order.pay_asset}
        </div>
        <div>network fee {String(order.network_fee_usd ?? "0")}</div>
        <div>total {String(order.total_usd ?? order.amount_usd)}</div>
        <div>
          qty {String(order.qty ?? 1)} · gift#{String(order.gift_product_id ?? "—")}
        </div>
      </div>
      {order.error && <p className="net-error">{order.error}</p>}
      <div className="shops-codes">
        {!codes.length && <p className="muted">—</p>}
        {codes.map((c) => (
          <div key={c} className="code-row">
            <code className="addr ltr">{c}</code>
            <button type="button" className="chip" onClick={() => onCopy(c)}>
              کپی
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
